import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useForm, FormProvider } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faHourglassStart, faLock, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import Select from '../Form/Fields/Select';
import Textarea from '../Form/Fields/Textarea';
import './OrderNotes.scss';
import './OrderInformation.scss';
import { API_BASE_URL } from '../../api';

const statusMap = {
  COMPLETE: { icon: faCheckCircle, label: 'Complète', variant: 'COMPLETE' },
  PENDING:  { icon: faHourglassStart, label: 'En attente', variant: 'PENDING' },
  CANCELED: { icon: faLock, label: 'Annulé', variant: 'CANCELED' },
};

const statusOptions = [
  { value: 'PENDING',  label: 'En attente' },
  { value: 'COMPLETE', label: 'Complète' },
  { value: 'CANCELED', label: 'Annulé' },
];

// Valid transitions: only forward moves, no regression
const ALLOWED_TRANSITIONS = {
  PENDING:  ['COMPLETE', 'CANCELED'],
  COMPLETE: ['CANCELED'],
  CANCELED: [],
};

const formatDateTime = (iso) => {
  if (!iso) return '';
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(iso));
};

const OrderNotesInner = ({ orderId, currentStatus, notes, onNoteAdded, onRefresh }) => {
  const isCanceled = currentStatus === 'CANCELED';

  // Current status stays selectable (= "no change") + valid next states only
  const availableOptions = statusOptions.filter(
    opt => opt.value === currentStatus || ALLOWED_TRANSITIONS[currentStatus]?.includes(opt.value)
  );

  const methods = useForm({
    defaultValues: { status: currentStatus || 'PENDING', comment: '' },
  });
  const { handleSubmit, control, reset } = methods;

  const onSubmit = async (data) => {
    if (!data.comment?.trim()) return;
    try {
      await axios.post(
        `${API_BASE_URL}/api/v1/orders/${orderId}/notes`,
        { status: data.status || null, comment: data.comment.trim() },
        { withCredentials: true }
      );
      reset({ status: data.status, comment: '' });
      if (data.status !== currentStatus) {
        onRefresh?.();
      } else {
        onNoteAdded();
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <FormProvider {...methods}>
      <div className="order-notes">
        {isCanceled ? (
          <div className="order-notes-canceled">
            <FontAwesomeIcon icon={faLock} />
            Cette vente est annulée. Aucune modification de statut possible.
          </div>
        ) : null}
        <form className="order-notes-form" onSubmit={handleSubmit(onSubmit)} style={isCanceled ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
          <Select
            label="Statut"
            name="status"
            options={availableOptions}
          />
          <Textarea
            name="comment"
            control={control}
            placeholder="Ajouter une note..."
          />
          <div className="order-notes-actions">
            <button type="submit" className="order-notes-submit">
              Ajouter
            </button>
          </div>
        </form>

        {notes.length > 0 && (
          <div className="order-notes-history">
            {notes.map(note => {
              const s = statusMap[note.status];
              return (
                <div key={note.id} className="order-note-entry">
                  <div className="order-note-meta">
                    <span className="order-note-author">{note.createdByUsername}</span>
                    <span className="order-note-date">{formatDateTime(note.createdAt)}</span>
                    {s && (
                      <span className={`order-status-badge order-status-badge--${s.variant}`}>
                        <FontAwesomeIcon icon={s.icon} />
                        {s.label}
                      </span>
                    )}
                  </div>
                  <p className="order-note-comment">{note.comment}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </FormProvider>
  );
};

const OrderNotes = ({ orderId, currentStatus, onRefresh }) => {
  const [notes, setNotes] = useState([]);

  const load = () => {
    axios.get(`${API_BASE_URL}/api/v1/orders/${orderId}/notes`, { withCredentials: true })
      .then(r => setNotes(r.data))
      .catch(() => {});
  };

  useEffect(() => { load(); }, [orderId]);

  return <OrderNotesInner orderId={orderId} currentStatus={currentStatus} notes={notes} onNoteAdded={load} onRefresh={onRefresh} />;
};

export default OrderNotes;
